// ProposalMain.jsx  (react-router-dom v5.3.4 대응 / useHistory + useLocation)
// - 최초 로딩 완료 전: 검색/초기화 비활성화 (입력은 가능)
// - 업로드 버튼/업로드 다이얼로그 제거
// - 목록 로딩 중: 리스트 영역에 "로딩 중입니다..." 표시
// - v5 전용: useNavigate 사용 X

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

// MUI
import {
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    TextField,
    Divider,
    Chip,
    Tooltip,
    CircularProgress, Container,
} from "@mui/material";

import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import PdfViewerModal from "./modal/PdfViewerModal";
import ProposalCard from "./ProposalCard";
import { ds } from "../../../styles/designSystem";
import { proposalApi } from "./api/proposalApi";
import LoadingDots from "../../../component/common/LoadingDots";
import ActionButton from "../../../component/common/ActionButton";
import ComboBox from "../../../component/common/ComboBox";
import { getApiErrorMessage } from "../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { notify } from "../../../util/toast";

const PAGE_SIZE = 24; // 스크롤 시 한 번에 더 보여줄 카드 수 (서버 재호출 없이 저장된 배열을 slice)

export default function ProposalMain() {
    const history = useHistory();
    const location = useLocation();

    // 검색 폼
    const [formData, setFormData] = useState({
        category: "",
        subCategory: "",
        year: "",
        name: "",
        keyword: "",
        tag: "",
    });

    // 원본 목록(서버)
    const [list, setList] = useState([]);

    // ✅ 최초 로딩 완료 여부(이게 true가 되기 전엔 검색/초기화 막기)
    const [listReady, setListReady] = useState(false);

    // ✅ 목록 로딩 상태
    const [listLoading, setListLoading] = useState(false);

    // PDF 미리보기 모달
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewPdf, setPreviewPdf] = useState(null);

    // 리스트 표시 여부
    const [showList, setShowList] = useState(false);

    // 무한 스크롤 — 저장된 목록을 스크롤에 따라 조금씩 렌더 (서버 재호출 X)
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const sentinelRef = useRef(null);

    // URLSearchParams (location.search 바뀔 때마다 새로 계산)
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // ========= 목록 불러오기 =========
    useEffect(() => {
        let mounted = true;

        const fetchList = async () => {
            try {
                setListLoading(true);

                const tagsParam = query.getAll("tags");
                const data = await proposalApi.getList({
                    category: query.get("category") || undefined,
                    subCategory: query.get("subCategory") || undefined,
                    year: query.get("year") || undefined,
                    name: query.get("name") || undefined,
                    keyword: query.get("keyword") || undefined,
                    tags: tagsParam.length > 0 ? tagsParam : undefined,
                });

                if (!mounted) return;
                setList(Array.isArray(data) ? data : []);
                setListReady(true);
            } catch (err) {
                console.error("목록 가져오기 실패:", err);
                if (!mounted) return;
                setList([]);
                // 실패여도 사용자가 계속 못쓰면 답답하니까 ready는 true로 올려서 검색/초기화 가능하게 처리
                setListReady(true);
            } finally {
                if (!mounted) return;
                setListLoading(false);
            }
        };

        fetchList();

        return () => {
            mounted = false;
        };
    }, [query]);

    // ========= location.search → 폼값 세팅 =========
    useEffect(() => {
        setFormData({
            category: query.get("category") || "",
            subCategory: query.get("subCategory") || "",
            year: query.get("year") || "",
            name: query.get("name") || "",
            keyword: query.get("keyword") || "",
            tag: query.getAll("tags").join(","),
        });

        // search가 바뀌면 목록 보여주기
        setShowList(true);
    }, [query]);

    // ========= 폼 입력 =========
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // 입력 중에는 "결과 표시 중" 대신 "입력 중"으로 보이게
        setShowList(false);
    }, []);

    // ========= 검색 =========
    const handleSearch = useCallback(
        (e) => {
            e.preventDefault();
            if (!listReady || listLoading) return; // ✅ 로딩 전/로딩 중 검색 금지

            const sp = new URLSearchParams();

            if (formData.category) sp.set("category", formData.category);
            if (formData.subCategory) sp.set("subCategory", formData.subCategory);
            if (formData.year) sp.set("year", formData.year);
            if (formData.name) sp.set("name", formData.name);
            if (formData.keyword) sp.set("keyword", formData.keyword);

            if (formData.tag) {
                formData.tag.split(",").forEach((t) => {
                    const trimmed = t.trim();
                    if (trimmed) sp.append("tags", trimmed);
                });
            }

            history.push({
                pathname: "/onandme/proposal",
                search: `?${sp.toString()}`,
            });

            setShowList(true);
        },
        [formData, history, listReady, listLoading]
    );

    // ========= 초기화 =========
    const handleReset = useCallback(() => {
        if (!listReady || listLoading) return; // ✅ 로딩 전/로딩 중 초기화 금지

        setFormData({
            category: "",
            subCategory: "",
            year: "",
            name: "",
            keyword: "",
            tag: "",
        });
        history.push("/onandme/proposal");
        setShowList(false);
    }, [history, listReady, listLoading]);

    // ========= 미리보기 =========
    const handlePreview = useCallback((item, pageNumber) => {
        const matchedFirstPage = pageNumber || item.matchedPages?.[0]?.page || 1;
        setPreviewPdf({
            path: item.path,
            initialPage: matchedFirstPage,
            matchedPages: item.matchedPages || [],
            name: item.name,
        });
        setIsPreviewOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsPreviewOpen(false);
        setPreviewPdf(null);
    }, []);

    // ========= 삭제 =========
    const handleDelete = useCallback(
        async (id) => {
            if (!(await confirmDialog({ message: "삭제하시겠습니까?" }))) return;
            try {
                await proposalApi.delete(id);
                notify("삭제되었습니다.", "success")

                // 지금 쿼리 그대로 유지해서 다시 로딩
                const currentQuery = new URLSearchParams(location.search).toString();
                history.replace(`/onandme/proposal?${currentQuery}`);
            } catch (error) {
                console.error("삭제 실패:", error);
                notify(getApiErrorMessage(error, "삭제에 실패했습니다."), "error")
            }
        },
        [history, location.search]
    );

    // ========= matchedPages → expandedList =========
    const expandedList = useMemo(() => {
        return (list || []).flatMap((item) => {
            if (!item.matchedPages || item.matchedPages.length === 0) return [item];
            return item.matchedPages.map((mp) => ({
                ...item,
                pageNumber: mp.page,
            }));
        });
    }, [list]);

    // 현재 화면에 렌더할 카드 — 저장된 expandedList 를 앞에서부터 visibleCount 만큼만
    const visibleItems = useMemo(
        () => expandedList.slice(0, visibleCount),
        [expandedList, visibleCount]
    );

    // 새 검색/필터 결과가 오면 처음부터 다시 보여준다.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [expandedList]);

    // 무한 스크롤 — sentinel 이 뷰포트(page)에 들어오면 저장된 배열에서 다음 묶음을 렌더.
    // 서버를 다시 부르지 않고 이미 받아둔 expandedList 를 slice 범위만 넓힌다.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || visibleCount >= expandedList.length) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount((v) => Math.min(v + PAGE_SIZE, expandedList.length));
                }
            },
            { rootMargin: "300px" } // 바닥 도달 전 미리 로드해 끊김 없이
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [visibleCount, expandedList.length]);

    // 현재 활성 필터 태그 칩
    const activeTags = useMemo(() => {
        const tags = (formData.tag || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
        return tags.slice(0, 8);
    }, [formData.tag]);

    const searchDisabled = !listReady || listLoading;

    return (

        <Box sx={{ ...ds.components.external.page, py: 6 }}>
            <Container maxWidth="full">

            <Box>

                    <Box>
                        <Typography sx={ds.components.external.heroTitle}>통합 제안서 검색</Typography>
                    </Box>

                    <br/>


                    {/* 검색 카드 */}
                    <Paper sx={{ ...ds.components.external.card, p: { xs: 2.25, md: 3 }, mb: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Typography sx={ds.typography.sectionTitle}>검색 필터</Typography>
                            <Chip
                                size="small"
                                label={showList ? "결과 표시 중" : "입력 중"}
                                variant="outlined"
                                sx={{ fontWeight: 800 }}
                            />
                            {/*{listLoading && (*/}
                            {/*    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>*/}
                            {/*        <CircularProgress size={18} />*/}
                            {/*        <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "text.secondary" }}>*/}
                            {/*            목록 불러오는 중*/}
                            {/*        </Typography>*/}
                            {/*    </Stack>*/}
                            {/*)}*/}
                        </Stack>

                        <Box component="form" onSubmit={handleSearch}>
                            <Stack spacing={2}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                    <ComboBox
                                        clearable={false}
                                        placeholder="카테고리"
                                        disabled={listLoading}
                                        options={[
                                            { value: "", label: "전체" },
                                            { value: "PROPOSAL", label: "PROPOSAL" },
                                            { value: "PRESENTATION", label: "PRESENTATION" },
                                        ]}
                                        value={formData.category}
                                        onChange={(v) => {
                                            setFormData((prev) => ({ ...prev, category: v }));
                                            setShowList(false);
                                        }}
                                    />

                                    <ComboBox
                                        clearable={false}
                                        placeholder="분류"
                                        disabled={listLoading}
                                        options={[
                                            { value: "", label: "전체" },
                                            { value: "MICE", label: "MICE" },
                                            { value: "VIDEO_PLANNING", label: "영상기획" },
                                        ]}
                                        value={formData.subCategory}
                                        onChange={(v) => {
                                            setFormData((prev) => ({ ...prev, subCategory: v }));
                                            setShowList(false);
                                        }}
                                    />

                                    <ComboBox
                                        clearable={false}
                                        placeholder="년도"
                                        disabled={listLoading}
                                        options={[
                                            { value: "", label: "전체" },
                                            ...Array.from({ length: 5 }, (_, i) => {
                                                const y = new Date().getFullYear() - i;
                                                return { value: String(y), label: String(y) };
                                            }),
                                        ]}
                                        value={formData.year}
                                        onChange={(v) => {
                                            setFormData((prev) => ({ ...prev, year: v }));
                                            setShowList(false);
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="행사 검색(포함 단어)"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        aria-disabled={listLoading}
                                    />
                                </Stack>

                                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                                    <TextField
                                        fullWidth
                                        label="제안서 내 키워드 검색"
                                        name="keyword"
                                        value={formData.keyword}
                                        onChange={handleChange}
                                        placeholder="키워드를 입력하세요"
                                        aria-disabled={listLoading}
                                    />
                                    <TextField
                                        fullWidth
                                        label="태그 (쉼표로 구분)"
                                        name="tag"
                                        value={formData.tag}
                                        onChange={handleChange}
                                        placeholder="예: MICE,부스,무대"
                                        aria-disabled={listLoading}
                                    />
                                </Stack>

                                {/* 태그 미리보기 */}
                                {activeTags.length > 0 && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {activeTags.map((t) => (
                                            <Chip key={t} label={t} size="small" variant="outlined" sx={ds.components.external.chipActive} />
                                        ))}
                                    </Stack>
                                )}

                                <Divider />

                                <Stack direction="row" spacing={1} justifyContent="flex-start">
                                    <ActionButton
                                        type="button"
                                        variant="secondary"
                                        startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
                                        onClick={handleReset}
                                        disabled={searchDisabled}
                                        label="초기화"
                                    />
                                    <ActionButton
                                        type="submit"
                                        variant="search"
                                        disabled={searchDisabled}
                                    />
                                </Stack>

                                {!listReady && (
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "text.secondary" }}>
                                        * 최초 목록 로딩이 끝나면 검색/초기화 버튼이 활성화됩니다.
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </Paper>
                </Box>

                {/* 리스트 — 페이지 스크롤 + 저장된 결과를 점진 렌더(무한 스크롤) */}
                {showList && (
                    <Paper sx={{ ...ds.components.external.card, p: 1.25, position: "relative" }}>
                        {listLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1.25, py: 10 }}>
                                <LoadingDots label="제안서 목록을 불러오고 있어요" />
                            </Box>
                        ) : expandedList.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 10, color: ds.colors.textMuted }}>
                                <Typography sx={{ fontSize: 14 }}>검색 결과가 없습니다</Typography>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: "grid",
                                        // minmax(0,1fr) — 1fr 기본값(min-width:auto)이면 카드 내용 최소너비만큼
                                        // 안 줄어 그리드가 컨테이너를 넘어 잘림. 0 으로 최소너비를 풀어 화면폭에 맞게 축소.
                                        // 한 줄에 2개 — 제안서는 '무엇이 들어있는지' 를 썸네일로 판단하는데
                                        // 4열에서는 슬라이드 글자가 읽히지 않는다는 요청. 열을 줄여 그만큼 크게 본다.
                                        gridTemplateColumns: {
                                            xs: "repeat(1, minmax(0, 1fr))",
                                            md: "repeat(2, minmax(0, 1fr))",
                                        },
                                        gap: 2.5,
                                        // 카드 높이는 이제 썸네일 비율이 정한다(고정 500px 제거) — 늘어난 폭만큼
                                        // 세로도 같이 커져야 슬라이드가 잘리지 않는다. stretch 면 카드마다
                                        // 제목 줄 수 차이로 썸네일 높이가 들쭉날쭉해져 start 로 고정.
                                        alignItems: "start",
                                    }}
                                >
                                    {visibleItems.map((item, i) => (
                                        <Box key={`${item.id ?? item.name ?? "p"}-${item.pageNumber ?? 0}-${i}`}>
                                            <ProposalCard
                                                item={item}
                                                pageNumber={item.pageNumber}
                                                onPreview={handlePreview}
                                                onDelete={handleDelete}
                                                fill
                                            />
                                        </Box>
                                    ))}
                                </Box>

                                {/* 무한 스크롤 sentinel — 화면에 들어오면 저장된 배열에서 다음 묶음 렌더 (재호출 X) */}
                                {visibleCount < expandedList.length && (
                                    <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                        <LoadingDots compact />
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>
                )}

                {/* PDF 미리보기 모달 */}
                {isPreviewOpen && previewPdf && (
                    <PdfViewerModal
                        isOpen={isPreviewOpen}
                        onClose={handleCloseModal}
                        path={previewPdf.path}
                        keyword={formData.keyword}
                        matchedPages={previewPdf.matchedPages || []}
                        initialPage={previewPdf.initialPage}
                    />
                )}
            </Container>
        </Box>
    );
}
